import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useAuth } from './context/AuthContext';
import { buildMonitorJson, extractImagePreview } from './utils/monitorUtils';
import MonitorFilters from './components/monitor/MonitorFilters';
import useMonitorFilters from './hooks/useMonitorFilters';

const HISTORY_PAGE_SIZE = 100;
const IMAGE_HISTORY_PAGE_SIZE = 10;

const createInitialState = () => ({
  entries: [],
  totalCount: 0,
  page: 0,
  expandedRowId: null,
  isLoading: true,
  error: null,
  pageSize: HISTORY_PAGE_SIZE
});

function resolveStatus(type, row) {
  if (type === 'web') {
    const executableData = Array.isArray(row.raw?.executable_data) ? row.raw.executable_data : [];
    const hasResults = executableData.length > 0;
    return {
      isHealthy: hasResults,
      tooltip: hasResults ? 'Web search results found' : 'No web search results'
    };
  }

  const isHealthy = row.status === 200;
  return {
    isHealthy,
    tooltip: isHealthy ? 'API healthy' : 'API issue'
  };
}

export default function MonitorHistoryPage({ type, title, variant }) {
  const { token } = useAuth();
  const [state, setState] = useState(createInitialState);
  
  // Filter state management
  const [appliedFilters, setAppliedFilters] = useState({
    statusCodes: { type: 'all', codes: [] },
    timePeriod: { type: 'all', startDate: null, endDate: null },
    service: 'all'
  });

  // Check if filters are active
  const hasActiveFilters = appliedFilters.statusCodes.type !== 'all' || appliedFilters.timePeriod.type !== 'all';

  const fetchLogs = useCallback(async (page, activeFilters = appliedFilters) => {
    if (!token) return;

    const pageSize = variant === 'imagex' ? IMAGE_HISTORY_PAGE_SIZE : HISTORY_PAGE_SIZE;

    setState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
      expandedRowId: null,
      pageSize
    }));

    try {
      // Build params directly with filters
      const params = new URLSearchParams({
        type,
        page: String(page),
        pageSize: String(pageSize)
      });
      
      if (variant) {
        params.set('variant', variant);
      }
      
      // Add filter parameters
      if (activeFilters.statusCodes.type !== 'all') {
        params.set('statusFilter', activeFilters.statusCodes.type);
        if (activeFilters.statusCodes.type === 'custom' && activeFilters.statusCodes.codes.length > 0) {
          params.set('statusCodes', activeFilters.statusCodes.codes.join(','));
        }
      }
      
      if (activeFilters.timePeriod.type !== 'all') {
        if (activeFilters.timePeriod.type === 'custom') {
          if (activeFilters.timePeriod.startDate) {
            params.set('startDate', activeFilters.timePeriod.startDate.toISOString());
          }
          if (activeFilters.timePeriod.endDate) {
            params.set('endDate', activeFilters.timePeriod.endDate.toISOString());
          }
        } else {
          params.set('timePeriod', activeFilters.timePeriod.type);
        }
      }

      const res = await fetch(`/api/monitor-logs?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!res.ok) {
        throw new Error(`Failed to load logs (status ${res.status})`);
      }

      const data = await res.json();
      const entries = Array.isArray(data.logs)
        ? data.logs.map((item) => ({
            ...item,
            id: item.id || item._id || `${type}-${item.timestamp}`,
            raw: item.raw ?? null,
            variant: item.variant || variant || 'mini'
          }))
        : [];

      setState({
        entries,
        totalCount: typeof data.total === 'number' ? data.total : entries.length,
        page,
        expandedRowId: null,
        isLoading: false,
        error: null,
        pageSize
      });
    } catch (err) {
      const message = err && err.message ? err.message : 'Failed to load logs';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
        pageSize
      }));
    }
  }, [token, type, variant, appliedFilters]);

  const handleApplyFilters = useCallback((newFilters) => {
    setAppliedFilters(newFilters);
    // Reset to first page when filters change
    fetchLogs(0, newFilters);
  }, [fetchLogs]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    fetchLogs(0);
  }, [fetchLogs, token]);

  const totalPages = useMemo(() => {
    const size = state.pageSize || HISTORY_PAGE_SIZE;
    return state.totalCount === 0 ? 1 : Math.ceil(state.totalCount / size);
  }, [state.totalCount, state.pageSize]);

  const goToPage = (nextPage) => {
    if (state.isLoading) return;
    const clamped = Math.max(0, Math.min(nextPage, totalPages - 1));
    if (clamped === state.page) return;
    fetchLogs(clamped);
  };

  const toggleExpansion = (rowId) => {
    setState((prev) => ({
      ...prev,
      expandedRowId: prev.expandedRowId === rowId ? null : rowId
    }));
  };

  return (
    <div className="container mx-auto p-4 pt-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          <p className="text-sm text-gray-500">
            Showing {state.pageSize} entries per page
            {hasActiveFilters && <span className="ml-2 text-blue-600">(filtered)</span>}
          </p>
        </div>
        <Link
          to="/monitor"
          className="hidden sm:inline-block px-3 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-100"
        >
          Back to Monitor
        </Link>
      </div>

      <Link
        to="/monitor"
        className="sm:hidden mb-4 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
      >
        Back to Monitor
      </Link>

      {/* Filter Component */}
      <MonitorFilters
        onApplyFilters={handleApplyFilters}
        initialFilters={appliedFilters}
        showTimePeriod={true}
        compact={false}
      />

      <div className="md:hidden space-y-4">
        {state.entries.length === 0 && !state.isLoading && hasActiveFilters && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <p className="text-gray-700 font-medium mb-2">No results to display</p>
            <p className="text-sm text-gray-600">
              No monitor entries match your current filters. Try adjusting your filter criteria.
            </p>
          </div>
        )}
        {state.entries.length === 0 && !state.isLoading && !hasActiveFilters && (
          <div className="text-center text-sm text-gray-500">No monitor results recorded yet.</div>
        )}
        {state.isLoading && state.entries.length === 0 && (
          <div className="text-center text-sm text-gray-500">Loading results...</div>
        )}
        {state.entries.map((row) => {
          const { isHealthy, tooltip } = resolveStatus(type, row);
          const haloColor = isHealthy ? 'bg-green-400' : 'bg-red-400';
          const dotColor = isHealthy ? 'bg-green-500' : 'bg-red-500';
          const isExpanded = state.expandedRowId === row.id;
          const previewSrc = isExpanded && row.raw ? extractImagePreview(row.raw) : null;
          const displayJson = isExpanded && row.raw ? buildMonitorJson(row.raw, row.variant) : '';

          return (
            <div key={row.id} className="rounded-lg border border-gray-200 shadow-sm p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-semibold text-gray-900">{new Date(row.timestamp).toLocaleString()}</div>
                <span className={`px-2 py-1 rounded-md text-xs ${
                  row.status >= 200 && row.status < 300
                    ? 'bg-green-100 text-green-700'
                    : row.status >= 400
                    ? 'bg-red-100 text-red-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {row.status}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs text-gray-600">
                <div>
                  <span className="block font-semibold text-gray-800 uppercase tracking-wide text-[11px]">Response Time</span>
                  <span className="block text-sm text-gray-900 mt-0.5">{(row.durationMs / 1000).toFixed(1)} sec</span>
                </div>
                <span className="relative inline-flex h-4 w-4 items-center justify-center" title={tooltip}>
                  <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${haloColor}`} />
                  <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${dotColor}`}>
                    <span className="sr-only">{tooltip}</span>
                  </span>
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-1">
                {row.status === 200 ? (
                  <span className="text-sm text-green-600 font-medium">200 OK</span>
                ) : row.raw ? (
                  <button
                    className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                    onClick={() => toggleExpansion(row.id)}
                    aria-expanded={isExpanded}
                    aria-controls={`mobile-history-row-json-${row.id}`}
                  >
                    {isExpanded ? 'Hide JSON' : 'View JSON'}
                  </button>
                ) : (
                  <span className="text-xs text-gray-400">No JSON</span>
                )}
              </div>

              {isExpanded && row.raw ? (
                <div className="space-y-3" id={`mobile-history-row-json-${row.id}`}>
                  {previewSrc ? (
                    <div className="overflow-hidden rounded-lg border border-slate-700">
                      <img src={previewSrc} alt="Generated output" className="w-full max-h-64 object-contain bg-black" />
                    </div>
                  ) : null}
                  <div className="rounded-lg border border-slate-700 bg-black/90">
                    <SyntaxHighlighter
                      language="json"
                      style={vscDarkPlus}
                      customStyle={{
                        margin: 0,
                        backgroundColor: 'transparent',
                        padding: '1rem',
                        fontSize: '0.75rem',
                        lineHeight: 1.5
                      }}
                      showLineNumbers
                      lineNumberStyle={{
                        color: '#64748B',
                        marginRight: '1rem'
                      }}
                      wrapLongLines
                      codeTagProps={{
                        style: {
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap'
                        }
                      }}
                      lineProps={{
                        style: {
                          wordBreak: 'break-word',
                          whiteSpace: 'pre-wrap'
                        }
                      }}
                    >
                      {displayJson || '{}'}
                    </SyntaxHighlighter>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {state.error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 text-red-700 text-sm">
          {state.error}
        </div>
      )}

      <div className="border rounded-lg bg-white shadow-sm">
        <div className="hidden md:block">
          <div className="overflow-x-auto overflow-y-auto max-h-[70vh] rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-blue-600">
              <tr>
                <th className="px-4 py-2 text-center text-xs font-semibold text-white uppercase tracking-wider">Timestamp</th>
                <th className="px-4 py-2 text-center text-xs font-semibold text-white uppercase tracking-wider">Status Code</th>
                <th className="px-4 py-2 text-center text-xs font-semibold text-white uppercase tracking-wider">Response Time (sec)</th>
                <th className="px-4 py-2 text-center text-xs font-semibold text-white uppercase tracking-wider">Output</th>
                <th className="px-4 py-2 text-center text-xs font-semibold text-white uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {state.entries.length === 0 && !state.isLoading && hasActiveFilters ? (
                <tr>
                  <td colSpan="5" className="px-4 py-6 text-center">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 inline-block">
                      <p className="text-gray-700 font-medium mb-2">No results to display</p>
                      <p className="text-sm text-gray-600">
                        No monitor entries match your current filters. Try adjusting your filter criteria.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : state.entries.length === 0 && !state.isLoading ? (
                <tr>
                  <td colSpan="5" className="px-4 py-6 text-center text-gray-500 text-sm">
                    No monitor results recorded yet.
                  </td>
                </tr>
              ) : (
                state.entries.map((row) => {
                  const { isHealthy, tooltip } = resolveStatus(type, row);
                  const haloColor = isHealthy ? 'bg-green-400' : 'bg-red-400';
                  const dotColor = isHealthy ? 'bg-green-500' : 'bg-red-500';
                  const isExpanded = state.expandedRowId === row.id;
                  const displayJson = isExpanded && row.raw ? buildMonitorJson(row.raw, row.variant) : '';

                  return (
                    <React.Fragment key={row.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap text-center">{new Date(row.timestamp).toLocaleString()}</td>
                        <td className="px-4 py-2 text-sm text-center">
                          <span className={`px-2 py-1 rounded-md text-xs ${
                            row.status >= 200 && row.status < 300
                              ? 'bg-green-100 text-green-700'
                              : row.status >= 400
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {row.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap text-center">{(row.durationMs / 1000).toFixed(1)} sec</td>
                        <td className="px-4 py-2 text-sm text-gray-700 text-center">
                          {row.status === 200 ? (
                            <span className="text-sm text-green-600 font-medium">200 OK</span>
                          ) : row.raw ? (
                            <button
                              className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                              onClick={() => toggleExpansion(row.id)}
                              aria-expanded={isExpanded}
                              aria-controls={`history-row-json-${row.id}`}
                            >
                              {isExpanded ? 'Hide JSON' : 'View JSON'}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">No JSON</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span className="relative inline-flex h-4 w-4 items-center justify-center" title={tooltip}>
                            <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${haloColor}`} />
                            <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${dotColor}`}>
                              <span className="sr-only">{tooltip}</span>
                            </span>
                          </span>
                        </td>
                      </tr>
                      {isExpanded && row.raw ? (
                        <tr>
                          <td colSpan="5" className="px-4 py-3 bg-black">
                            <div
                              id={`history-row-json-${row.id}`}
                              className="overflow-auto max-h-96 rounded-lg border border-slate-700 shadow-inner p-4"
                            >
                              <SyntaxHighlighter
                                language="json"
                                style={vscDarkPlus}
                                customStyle={{
                                  margin: 0,
                                  backgroundColor: 'transparent',
                                  padding: 0,
                                  fontSize: '0.75rem',
                                  lineHeight: 1.5
                                }}
                                showLineNumbers
                                lineNumberStyle={{
                                  color: '#64748B',
                                  marginRight: '1rem'
                                }}
                                wrapLongLines
                                codeTagProps={{
                                  style: {
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap'
                                  }
                                }}
                                lineProps={{
                                  style: {
                                    wordBreak: 'break-word',
                                    whiteSpace: 'pre-wrap'
                                  }
                                }}
                              >
                                {displayJson || '{}'}
                              </SyntaxHighlighter>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })
              )}
              {state.isLoading && (
                <tr>
                  <td colSpan="5" className="px-4 py-3 text-center text-sm text-gray-500">
                    Loading results...
                  </td>
                </tr>
              )}
            </tbody>
            </table>
          </div>
        </div>
        {state.totalCount > 0 && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-t bg-gray-50 text-sm text-gray-600">
            <span>
              Page {state.page + 1} of {totalPages} &bull; Total records: {state.totalCount}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="px-3 py-1 border border-gray-300 rounded-md bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => goToPage(state.page - 1)}
                disabled={state.page === 0 || state.isLoading}
              >
                Previous
              </button>
              <button
                type="button"
                className="px-3 py-1 border border-gray-300 rounded-md bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => goToPage(state.page + 1)}
                disabled={state.page >= totalPages - 1 || state.isLoading}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
